"use client";

// selector-markers.ts — marker derivation for the site-wide mountain selector modal. The geo fetch and
// marker memo below are the modal's own code moved verbatim; nothing
// about what a marker carries — real /api/transport/geo coordinates, the catalogue's bookability fact,
// the list's own destination, the served bubble content, the airport place dedupe — changed.

import { useEffect, useMemo, useState } from "react";
import type { KeylessMarker } from "@/components/map/KeylessMap";
import { airportPlaceName, airportStopsByPlace } from "@/lib/airport-places";
import { RESORTS } from "./resorts";
import { mountainCodeFromPath } from "./hub-nav";
import { destinationFor } from "./switch-intent";
import type { readSwitchIntent } from "./switch-intent";
import { bookabilityOf } from "./mountain-catalogue";

interface GeoMountain { code: string; name: string; lat: number; lng: number }
interface GeoStop { code: string; name: string; lat: number; lng: number }

export function useSelectorMarkers({
  active,
  meta,
  listingCounts,
  bookable,
  switchIntent,
}: {
  active: boolean;
  meta: Record<string, { logoUrl?: string | null } | undefined>;
  listingCounts: Record<string, number | undefined>;
  bookable: Set<string> | null;
  switchIntent: ReturnType<typeof readSwitchIntent>;
}): { geo: { mountains: GeoMountain[]; stops: GeoStop[] }; markers: KeylessMarker[] } {
  const open = active;
  const [geo, setGeo] = useState<{ mountains: GeoMountain[]; stops: GeoStop[] }>({ mountains: [], stops: [] });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    // the mountain list, logos, bookability and listing counts all arrive via `useMountainCatalogue`
    // above (S35 · F226). What stays here is the GEO fetch, because only this surface draws a map.
    // map coords (real lat/lng from the legacy DB via the adapter)
    fetch("/api/transport/geo")
      .then((r) => r.json() as Promise<{ mountains?: unknown; stops?: unknown } | null>)
      .then((j) => {
        if (!alive) return;
        setGeo({
          mountains: Array.isArray(j?.mountains) ? (j.mountains as GeoMountain[]) : [],
          stops: Array.isArray(j?.stops) ? (j.stops as GeoStop[]) : [],
        });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [open]);

  const markers: KeylessMarker[] = useMemo(() => {
    // geo speaks mountain CODES; the routing table speaks resort HREFS. A code with no resort route gets
    // NO href rather than a guessed one, and that pin then keeps the pre-F209 click-opens-bubble
    // behaviour instead of navigating somewhere we cannot name.
    const hrefByCode = new Map<string, string>();
    for (const r of RESORTS) {
      const c = mountainCodeFromPath(r.href);
      if (c) hrefByCode.set(c, r.href);
    }
    const mtns: KeylessMarker[] = geo.mountains.map((m) => {
      const n = listingCounts[m.code];
      const resortHref = hrefByCode.get(m.code);
      // S36 card 1 — THE MAP READS THE LIST'S OWN FACT. `bookabilityOf` is the same call the row makes,
      // against the same served `bookable` set, so "yellow in the list, yellow on the map" is true by
      // construction rather than by two code paths agreeing. `unknown` passes no status at all and the
      // pin keeps its neutral pink glyph — the map's equivalent of the row rendering no lamp.
      const mapStatus = bookabilityOf(m.code, bookable);
      return {
        lat: m.lat,
        lng: m.lng,
        name: m.name,
        key: `m:${m.code}`,
        status: mapStatus === "unknown" ? undefined : mapStatus,
        href: resortHref ? destinationFor(resortHref, switchIntent) : undefined,
        bubble: {
          kind: "mountain" as const,
          title: m.name,
          logoUrl: meta[m.code]?.logoUrl ?? undefined,
          stat: typeof n === "number" ? `${n} listing${n === 1 ? "" : "s"}` : undefined,
        },
      };
    });
    // S35 · F204 — "airports missing: only ONE renders" — CHECKED AGAINST THE SERVED STOPS, and the
    // finding's premise inverts. `/api/transport/geo` serves 22 stops and exactly ONE physical airport:
    //   yvr-vancouver-airport | yvr-vancouver-airport-express | yvr-vancouver-airport-executive
    // Three SERVICE-TIER rows for one place. The name filter matched all three, so the map was drawing
    // three plane pins stacked on a single coordinate — which is why one is all you can ever see. There
    // is no second airport hiding behind a filter: the transport network we serve flies out of YVR only.
    // So the honest fix is the opposite of "find the missing ones" — it is to stop drawing one place
    // three times. NO AIRPORT IS INVENTED HERE; if the served set ever gains a real second airport it
    // appears automatically, because the dedupe key is the COORDINATE, not a name we hardcoded.
    //
    // S36 card 1 · F275 — THE DEDUPE FAILED ON THE VERY CASE IT WAS WRITTEN FOR, and measuring it was
    // the first half of the founder's "no airport pins at all". The served set is three rows:
    //   yvr-vancouver-airport            49.1950, -123.1802
    //   yvr-vancouver-airport-express    49.1950, -123.1802   ← collapsed by the coordinate key
    //   yvr-vancouver-airport-executive  49.1967, -123.1815   ← 190m away, so it SURVIVED it
    // 190m is more than the 11m the `toFixed(4)` key allows, so the EXECUTIVE tier came through as a
    // "second airport" — and at the fitted continental zoom it landed on the identical pixel box
    // [775,406,26,30], one pin drawn exactly on top of another. The premise the coordinate key rests on
    // (that fare tiers of one place share a coordinate) is simply not true of this feed.
    // So the key is the PLACE, derived from the name with the fare-tier suffix removed — the same
    // suffix strip the label below already performs, promoted to the thing that decides identity. It
    // still invents no airport: two genuinely different airports have different names and both render.
    //
    // S37 — THE RULE MOVED TO `@/lib/airport-places` AND NOTHING ABOUT IT CHANGED. It was inlined here,
    // which is why `TransportMountainDirectory` — the OTHER component drawing this same feed, through
    // PortMap — still rendered all three tiers a sprint after F275 was called fixed. Same class as F192's
    // two marker files and F191's two bookability chips: a rule that lives at one call site gets fixed at
    // one call site. Both maps imported it, so a third surface inherits the fix by construction.
    // (DEAD-SELECTOR-CLEANUP-20260925: the directory page is retired and deleted; this is the live caller.)
    const airports: KeylessMarker[] = airportStopsByPlace(geo.stops).map((s) => ({
      lat: s.lat,
      lng: s.lng,
      // the tier suffix — "(EXPRESS)" / "(EXECUTIVE)" — is a fare product, not a place. Stripped so
      // the label names the airport rather than whichever tier happened to sort first.
      name: airportPlaceName(s.name),
      key: `a:${s.code}`,
      flag: "plane" as const,
    }));
    return [...mtns, ...airports];
    // S36 card 1 — `bookable` IS a dependency and was missing. It worked only by accident: the catalogue
    // sets `bookable` and `listingCounts` in the same batched `.then`, so the counts object dragged the
    // memo along. Serve `{ok:true, mountains:[...]}` with `counts` absent and the guard skips
    // `setListingCounts`, nothing in the old dep list changes, and every row paints its lamp while every
    // pin renders neutral pink — the exact list/map second source this card exists to remove.
  }, [geo, meta, listingCounts, bookable, switchIntent]);

  return { geo, markers };
}
